import { useState } from 'react';
import { HelperText, PasswordInput, Spacer, TextField } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.scss';

type WifiFormValues = {
  wifiName: string;
  newPasswordValue: string;
  retypePasswordValue: string;
};

type WifiFormHandlers = {
  onNameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
};

type WifiFormValidation = {
  isSsidNameValid: boolean;
  isPasswordLengthValid: boolean;
  doPasswordsMatch: boolean;
};

type WifiFormProps = {
  values: WifiFormValues;
  handlers: WifiFormHandlers;
  validation: WifiFormValidation;
  loading: boolean;
  canSave: boolean;
};

export const WifiForm = ({ values, handlers, validation, loading, canSave }: WifiFormProps) => {
  const { t } = useTranslation('Network');
  const { wifiName, newPasswordValue, retypePasswordValue } = values;
  const { onNameChange, onPasswordChange, onConfirmPasswordChange } = handlers;
  const { isSsidNameValid, isPasswordLengthValid, doPasswordsMatch } = validation;
  const [hasNameChanged, setHasNameChanged] = useState(false);

  const isPasswordHelperInfo = isPasswordLengthValid || newPasswordValue.length === 0;

  return (
    <>
      <TextField
        fluid
        required
        name="wifiName"
        id="wifiName"
        data-cy="wifiName-field"
        value={wifiName}
        labelProps={{
          showStar: true,
          optionalText: '',
          labelText: t('networkManagement.wifiNameLabel'),
        }}
        onChange={(e) => {
          setHasNameChanged(true);
          onNameChange(e.currentTarget.value);
        }}
      />
      <HelperText
        state={!hasNameChanged || isSsidNameValid ? 'info' : 'error'}
        helpMessage={
          !hasNameChanged || isSsidNameValid
            ? t('networkManagement.wifiNameHelper')
            : t('networkManagement.wifiNameError')
        }
        style={{ marginTop: '8px', marginBottom: '24px' }}
      />
      <div className={styles['divider']} />

      <Spacer y={'24px'} />
      <PasswordInput
        fluid
        required
        enablePasswordShow
        name="newWifiPassword"
        id="newWifiPassword"
        value={newPasswordValue}
        labelProps={{
          showStar: true,
          optionalText: '',
          labelText: t('networkManagement.wifiPasswordFieldLabel'),
        }}
        onChange={(e) => onPasswordChange(e.currentTarget.value)}
      />
      <HelperText
        state={isPasswordHelperInfo ? 'info' : 'error'}
        helpMessage={
          isPasswordHelperInfo ? t('networkManagement.wifiPasswordHelper') : t('networkManagement.wifiPasswordError')
        }
        style={{ marginTop: '8px' }}
      />
      <Spacer y={'24px'} />
      <PasswordInput
        fluid
        required
        enablePasswordShow
        name="retypeWifiPassword"
        id="retypeWifiPassword"
        value={retypePasswordValue}
        labelProps={{
          showStar: true,
          optionalText: '',
          labelText: t('networkManagement.wifiPasswordRetypeFieldLabel'),
        }}
        onChange={(e) => onConfirmPasswordChange(e.currentTarget.value)}
      />

      {isPasswordLengthValid && retypePasswordValue.length > 0 && !doPasswordsMatch && (
        <HelperText
          state="error"
          helpMessage={t('networkManagement.wifiPasswordMatchError')}
          style={{ marginTop: '8px' }}
        />
      )}
    </>
  );
};
